// Navigator three-column layout.
//
//   .nav-sidebar (260px) | .nav-main (flex)   | .nav-details (40vw)
//   list tree            | poster cover       | title details + rating
//
// Selection lives in URL search params (?list=queue&title=tt0133093); the
// title detail is fetched once here and shared by the cover + details panes.
//
// Rate mode (?rate=1, entered via the header "rate" button) steps through the
// queue like the TUI's rate command: the head of the queue is auto-selected,
// and after each rating/skip removes it, selection advances to the next item.
// Clicking a different queue row in the tree continues the flow from there.

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useListItems, useTitle } from "../api/hooks";
import { loadLayout } from "../api/layout";
import {
  cycleField,
  type FilterState,
  loadSort,
  saveSort,
  type SortState,
  viewItems,
} from "../api/sort";
import { type ListName } from "../api/types";
import { CoverPane } from "../components/CoverPane";
import { DetailsPanel, type RateModeState } from "../components/DetailsPanel";
import { ListTree } from "../components/ListTree";

import "./Navigator.css";

export function Navigator() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("title");
  const selectedList = (params.get("list") as ListName | null) ?? null;
  const rateMode = params.get("rate") === "1";
  const { data: title, error, isLoading } = useTitle(selectedId);
  const { data: queue } = useListItems(rateMode ? "queue" : null);

  // Panel-side preference (read once on mount; changing it in Settings takes
  // effect when this route remounts).
  const swapped = useMemo(() => loadLayout() === "swapped", []);

  // Sort + filter apply to every list — owned here so the tree display,
  // arrow-key nav, and advance-in-source all agree on the order.
  const [sort, setSort] = useState<SortState>(loadSort);
  const [filter, setFilter] = useState<FilterState | null>(null);
  function updateSort(next: SortState) {
    setSort(next);
    saveSort(next);
  }
  function cycleSortField() {
    const field = cycleField(sort.field);
    updateSort({ ...sort, field });
    setFilter(null); // a filter is field-specific — clear it when the field changes
  }
  function setFilterValue(value: string | null) {
    setFilter(value == null ? null : { field: sort.field, value });
  }

  // Arrow-key navigation: ↑/↓ cycle through the currently selected list
  // (wrapping at the ends). Fetch the selected list's items independently of
  // the tree's expansion state so the keys work even when the node is collapsed.
  const { data: currentList } = useListItems(selectedList);
  const sortedSiblings = useMemo(
    () => viewItems(currentList?.items ?? [], sort, filter),
    [currentList, sort, filter],
  );
  useEffect(() => {
    const items = sortedSiblings;
    if (!selectedList || items.length === 0) return;

    function onKey(e: KeyboardEvent) {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      const idx = selectedId ? items.findIndex((i) => i.imdb_id === selectedId) : -1;
      const nextIdx =
        e.key === "ArrowDown"
          ? idx < 0
            ? 0
            : (idx + 1) % items.length
          : idx < 0
            ? items.length - 1
            : (idx - 1 + items.length) % items.length;
      const next = new URLSearchParams(params);
      next.set("list", selectedList!);
      next.set("title", items[nextIdx].imdb_id);
      setParams(next, { replace: true });
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sortedSiblings, selectedId, selectedList, params, setParams]);

  const queueItems = queue?.items ?? [];
  const queueIndex = selectedId
    ? queueItems.findIndex((i) => i.imdb_id === selectedId)
    : -1;

  // Rate mode: keep a queue item selected. When the current selection leaves
  // the queue (rated or skipped away) — or nothing is selected yet — advance
  // to the queue head.
  const prevQueueLen = useRef<number | null>(null);
  useEffect(() => {
    if (!rateMode || !queue) return;
    const len = queueItems.length;
    if (len === 0) {
      // Clear the selection exactly ONCE, at the moment the queue empties, so
      // the "queue finished" screen shows. Do NOT keep clearing afterwards, or
      // the user can't click an entry to (re)rate it — it would flash away.
      if (prevQueueLen.current !== null && prevQueueLen.current > 0 && selectedId != null) {
        const next = new URLSearchParams(params);
        next.delete("title");
        setParams(next, { replace: true });
      }
      prevQueueLen.current = 0;
      return;
    }
    prevQueueLen.current = len;
    if (queueIndex === -1) {
      const next = new URLSearchParams(params);
      next.set("list", "queue");
      next.set("title", queueItems[0].imdb_id);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateMode, queue, selectedId]);

  function exitRateMode() {
    const next = new URLSearchParams(params);
    next.delete("rate");
    setParams(next, { replace: true });
  }

  const rateState: RateModeState | null = rateMode
    ? {
        position: queueIndex >= 0 ? queueIndex + 1 : 0,
        total: queueItems.length,
        onExit: exitRateMode,
      }
    : null;

  return (
    <div className={"nav-shell" + (swapped ? " nav-shell--swapped" : "")}>
      <aside className="nav-sidebar">
        <div className="nav-sidebar__inner">
          <ListTree
            sort={sort}
            filter={filter}
            onCycleField={cycleSortField}
            onToggleDir={() => updateSort({ ...sort, dir: sort.dir === "asc" ? "desc" : "asc" })}
            onSetFilter={setFilterValue}
          />
        </div>
      </aside>

      <section className="nav-main">
        <CoverPane imdbId={selectedId} title={title} />
      </section>

      <aside className="nav-details">
        <DetailsPanel
          imdbId={selectedId}
          title={title}
          error={error}
          isLoading={isLoading}
          rateMode={rateState}
          siblings={sortedSiblings}
        />
      </aside>
    </div>
  );
}
